/**
 * Y GO Shuttle Services France - Croisières & Excursions
 * Page avec nouveau design "brushed gold relief" et intégration Bókun API
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Ship, Camera, MapPin, Clock, Calendar, Phone, Users, Check, X, Mail, Loader2 } from 'lucide-react';
import { Header, Footer } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useCMSPage } from '../hooks/useCMSPage';
import SafeImage from '../components/SafeImage';
import config from 'config';

const API_URL = config.API_URL;

const FALLBACK_IMAGES = {
  hero: 'https://images.unsplash.com/photo-1521292270410-a8c53642e9d0?auto=format&fit=crop&w=1600&q=80',
  etretat: 'https://images.unsplash.com/flagged/photo-1569750928069-a9e87dacd575?auto=format&fit=crop&w=900&q=80',
  honfleur: 'https://images.unsplash.com/photo-1704367630537-8d0dbc0d67ec?auto=format&fit=crop&w=900&q=80',
  deauville: 'https://images.unsplash.com/photo-1675774196611-9a342a727e5f?auto=format&fit=crop&w=900&q=80',
  dday: 'https://images.unsplash.com/photo-1683375805925-4a896ce6e2f5?auto=format&fit=crop&w=900&q=80',
  montSaintMichel: 'https://images.unsplash.com/photo-1505857807163-e4c177d2c3f1?auto=format&fit=crop&w=900&q=80',
  giverny: 'https://images.unsplash.com/photo-1585137439294-53e8c57ab243?auto=format&fit=crop&w=900&q=80',
};

export default function CroisieresExcursionsPage() {
  const { content: cmsContent, loading: cmsLoading } = useCMSPage('croisieres-excursions');
  const [selectedExcursion, setSelectedExcursion] = useState(null);
  const [bokunProduct, setBokunProduct] = useState(null);
  const [loadingBokunProduct, setLoadingBokunProduct] = useState(false);
  const [bookingData, setBookingData] = useState({
    customer_name: '',
    user_email: '',
    customer_phone: '',
    booking_date: '',
    number_of_participants: 1,
    special_requests: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingError, setBookingError] = useState(null);

  // Extraire les excursions du CMS
  const getExcursionsFromCMS = () => {
    if (!cmsContent?.sections) return [];
    
    const excursionsSection = cmsContent.sections.find(s => 
      s.title?.toLowerCase().includes('excursion') && s.items && s.items.length > 0
    );
    
    if (excursionsSection && excursionsSection.items) {
      return excursionsSection.items.map(item => ({
        ...item,
        id: item.id || item.title?.toLowerCase().replace(/\s+/g, '-'),
        image: item.image_url || FALLBACK_IMAGES[item.id],
      }));
    }
    
    return [];
  };

  const excursions = getExcursionsFromCMS();

  // Charger les données du produit Bókun quand une excursion est sélectionnée
  useEffect(() => {
    const loadBokunProduct = async () => {
      if (selectedExcursion && selectedExcursion.bokun_product_id) {
        setLoadingBokunProduct(true);
        setBokunProduct(null);
        try {
          const response = await fetch(`${API_URL}/api/bokun/products/${selectedExcursion.bokun_product_id}`);
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.product) {
              setBokunProduct(data.product);
            }
          }
        } catch (error) {
          console.error('Erreur chargement produit Bókun:', error);
        } finally {
          setLoadingBokunProduct(false);
        }
      } else {
        setBokunProduct(null);
      }
    };

    loadBokunProduct();
  }, [selectedExcursion]);

  // Gérer la soumission de réservation
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setBookingError(null);

    try {
      const response = await fetch(`${API_URL}/api/bokun/bookings?product_id=${selectedExcursion.bokun_product_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        setBookingSuccess(true);
        setTimeout(() => {
          setSelectedExcursion(null);
          setBookingSuccess(false);
          setBookingData({
            customer_name: '',
            user_email: '',
            customer_phone: '',
            booking_date: '',
            number_of_participants: 1,
            special_requests: ''
          });
        }, 3000);
      } else {
        const error = await response.json();
        setBookingError(error.detail || 'Erreur lors de la réservation');
      }
    } catch (error) {
      setBookingError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  // Get hero data from CMS
  const banner = cmsContent?.banner || {};
  const heroImage = banner.image_url || FALLBACK_IMAGES.hero;
  const heroTitle = banner.title || 'Excursions privées depuis le terminal croisières';
  const heroSubtitle = banner.subtitle || 'Croisiéristes • Le Havre • Normandie';
  const heroDescription = banner.description || 'Chauffeur privé dédié, retour garanti avant l\'heure d\'embarquement et itinéraires pensés pour tirer le meilleur parti de votre escale au Havre.';

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050509]">
      <Header />
      <main>
        {/* HERO */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <SafeImage 
              src={heroImage}
              fallbackSrc={FALLBACK_IMAGES.hero}
              alt="Terminal croisières du Havre"
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, rgba(11,11,11,0.9) 0%, rgba(11,11,11,0.7) 40%, rgba(11,11,11,0) 80%)'
              }}
            />
          </div>

          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-3xl space-y-6">
              <p className="text-[10px] font-medium tracking-[0.35em] uppercase text-[#C9A24D]">
                {heroSubtitle}
              </p>
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#E5E5E5] leading-tight" style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}>
                {heroTitle.includes('terminal') ? (
                  <>
                    {heroTitle.split('terminal croisières')[0]}
                    <span className="text-[#C9A24D]">terminal croisières</span>
                    {heroTitle.split('terminal croisières')[1]}
                  </>
                ) : heroTitle}
              </h1>
              <p className="text-[#E5E5E5]/80 text-base sm:text-lg leading-relaxed max-w-2xl">
                {heroDescription}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Link to="/booking">
                  <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-8 py-4 text-base rounded-none w-full sm:w-auto">
                    <Calendar className="w-5 h-5 mr-2" />
                    Réserver une excursion
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 px-8 py-4 text-base rounded-none w-full sm:w-auto"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Demander un devis sur mesure
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap gap-4 pt-4 text-sm text-[#E5E5E5]/80">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#C9A24D]" />
                  <span>Chauffeurs bilingues</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#C9A24D]" />
                  <span>Retour garanti à l'heure</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#C9A24D]" />
                  <span>Itinéraires personnalisables</span>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-[#C9A24D]/50 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-[#C9A24D] rounded-full mt-2 animate-pulse" />
            </div>
          </div>
        </section>

        {/* EXCURSIONS */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Nos excursions privées en <span className="text-[#C9A24D]">Normandie</span>
              </h2>
              <p className="text-[#A7A7A7] text-base">
                Tous nos circuits sont réalisés en véhicule privé avec chauffeur bilingue, au départ et au retour du terminal croisières du Havre.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {excursions.map((excursion) => (
                <button
                  key={excursion.id}
                  type="button"
                  onClick={() => setSelectedExcursion(excursion)}
                  className="group text-left relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all duration-300 flex flex-col backdrop-blur-sm"
                >
                  <div className="relative h-48 w-full overflow-hidden">
                    <SafeImage
                      src={excursion.image}
                      fallbackSrc={FALLBACK_IMAGES.dday}
                      alt={excursion.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-transparent to-transparent" />
                    <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 text-xs text-[#F5F5F5] bg-[#0B0B0B]/80 px-3 py-1.5">
                      <Clock className="w-3 h-3 text-[#C9A24D]" />
                      {excursion.duration}
                    </span>
                  </div>

                  <div className="p-5 flex flex-col justify-between flex-1">
                    <div>
                      <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5] mb-2 line-clamp-2">
                        {excursion.title}
                      </h3>
                      <p className="text-sm text-[#A7A7A7] leading-relaxed mb-3 line-clamp-3">{excursion.description}</p>
                      <div className="flex items-center gap-2 text-xs text-[#D4D4D4] mb-3">
                        <Ship className="w-4 h-4 text-[#C9A24D]" />
                        <span>Terminal croisières du Havre</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-[#C9A24D]/10">
                      <span className="text-[#C9A24D] font-semibold text-sm">Sur demande</span>
                      <span className="text-[#C9A24D] text-xs font-medium group-hover:text-[#E6C77A]">
                        Voir les détails →
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="text-center mt-10">
              <p className="text-[#A7A7A7] text-sm mb-4">
                Vous ne trouvez pas votre destination idéale ? Nous créons des itinéraires sur mesure.
              </p>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 px-8 py-3 rounded-none text-sm"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Demander un itinéraire personnalisé
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* GARANTIES */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Pensé pour les <span className="text-[#C9A24D]">croisiéristes internationaux</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Que vous voyagiez en couple, en famille ou en petit groupe, nous adaptons le véhicule, le rythme et les points d'intérêt à vos attentes.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-8 backdrop-blur-sm">
                <div className="mb-6">
                  <div className="w-12 h-12 bg-[#C9A24D]/12 flex items-center justify-center mb-4">
                    <Check className="w-6 h-6 text-[#C9A24D]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#F5F5F5] mb-2">
                    Nos garanties
                  </h3>
                  <p className="text-[#A7A7A7] text-sm">
                    Service premium dédié aux croisiéristes avec engagement de ponctualité
                  </p>
                </div>
                <div className="space-y-4 text-sm text-[#D4D4D4]">
                  {[
                    'Chauffeurs bilingues français / anglais',
                    'Accueil au terminal avec panneau nominatif',
                    'Horaires ajustés à l\'heure réelle d\'accostage',
                    'Retour garanti avant l\'heure limite d\'embarquement',
                    'Véhicules premium climatisés et équipés',
                    'Assistance téléphonique 24h/24 pendant l\'excursion',
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <Check className="w-4 h-4 mt-0.5 text-[#C9A24D] flex-shrink-0" />
                      <span className="leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-8 backdrop-blur-sm">
                <div className="mb-6">
                  <div className="w-12 h-12 bg-[#C9A24D]/12 flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-[#C9A24D]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#F5F5F5] mb-2">
                    Capacité & organisation
                  </h3>
                  <p className="text-[#A7A7A7] text-sm">
                    Idéal pour 2 à 8 passagers par véhicule, avec possibilité de flotte étendue
                  </p>
                </div>
                <ul className="space-y-4 text-sm text-[#D4D4D4] mb-8">
                  <li className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 mt-0.5 text-[#C9A24D] flex-shrink-0" />
                    <span className="leading-relaxed">Prise en charge au terminal, à l'hôtel ou en centre‑ville</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Camera className="w-4 h-4 mt-0.5 text-[#C9A24D] flex-shrink-0" />
                    <span className="leading-relaxed">Temps libre sur place pour profiter des visites, photos et pauses cafés</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="w-4 h-4 mt-0.5 text-[#C9A24D] flex-shrink-0" />
                    <span className="leading-relaxed">Durée adaptée à la fenêtre d'escale (demi‑journée ou journée complète)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Ship className="w-4 h-4 mt-0.5 text-[#C9A24D] flex-shrink-0" />
                    <span className="leading-relaxed">Suivi en temps réel de votre navire et ajustements si nécessaire</span>
                  </li>
                </ul>
                <a href="tel:+33278780404" className="block">
                  <Button className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold rounded-none text-sm py-3">
                    <Phone className="w-4 h-4 mr-2" />
                    Parler à un conseiller excursions
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-20 bg-gradient-to-r from-[#111111] to-[#1F1F1F] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
              Profitez pleinement de votre escale au <span className="text-[#C9A24D]">Havre</span>
            </h2>
            <p className="text-[#A7A7A7] text-base mb-8 max-w-2xl mx-auto">
              Nous construisons avec vous l'excursion idéale, au rythme de votre groupe, avec un chauffeur privé dédié et un retour garanti à l'heure.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/booking">
                <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-8 py-4 rounded-none text-base">
                  <Calendar className="w-5 h-5 mr-2" />
                  Réserver une excursion
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 px-8 py-4 rounded-none text-base"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Demander un devis détaillé
                </Button>
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mt-12 max-w-2xl mx-auto">
              <a 
                href="tel:+33278780404"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 text-center backdrop-blur-sm group"
              >
                <Phone className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
                <p className="text-[#E5E5E5] font-medium group-hover:text-[#C9A24D] transition-colors">
                  +33 2 78 78 04 04
                </p>
                <p className="text-[#A7A7A7] text-xs mt-1">Assistance 24/7</p>
              </a>
              <a 
                href="mailto:booking@y-go.fr"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 text-center backdrop-blur-sm group"
              >
                <Mail className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
                <p className="text-[#E5E5E5] font-medium group-hover:text-[#C9A24D] transition-colors">
                  booking@y-go.fr
                </p>
                <p className="text-[#A7A7A7] text-xs mt-1">Réponse sous 2h</p>
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* MODAL PRODUIT BÓKUN - Formulaire Custom */}
      {selectedExcursion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => {
              setSelectedExcursion(null);
              setBokunProduct(null);
              setBookingSuccess(false);
              setBookingError(null);
            }}
          />
          <div className="relative z-10 w-full max-w-4xl bg-[#050509] border border-[#C9A24D]/30 shadow-[0_18px_45px_rgba(0,0,0,0.85)] max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 z-20 bg-[#0B0B0B] border-b border-[#C9A24D]/20 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#C9A24D]/12 flex items-center justify-center">
                  <Ship className="w-5 h-5 text-[#C9A24D]" />
                </div>
                <div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5]">
                    {bokunProduct?.title || selectedExcursion.title}
                  </h3>
                  <p className="text-xs text-[#A7A7A7]">Excursion privée depuis le terminal croisières</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedExcursion(null);
                  setBokunProduct(null);
                  setBookingSuccess(false);
                  setBookingError(null);
                }}
                className="text-[#A7A7A7] hover:text-[#F5F5F5] transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Image principale */}
            <div className="relative h-64 overflow-hidden">
              <SafeImage
                src={
                  bokunProduct?.keyPhoto?.derived?.find(d => d.name === 'large')?.url ||
                  bokunProduct?.photos?.[0]?.derived?.find(d => d.name === 'large')?.url ||
                  selectedExcursion.image
                }
                fallbackSrc={FALLBACK_IMAGES.dday}
                alt={bokunProduct?.title || selectedExcursion.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050509] via-transparent to-transparent" />
              <div className="absolute bottom-4 left-6 flex gap-3">
                <span className="inline-flex items-center gap-2 bg-[#0B0B0B]/90 backdrop-blur-sm px-4 py-2 text-sm">
                  <Clock className="w-4 h-4 text-[#C9A24D]" />
                  <span className="text-[#F5F5F5]">
                    {bokunProduct?.durationText || selectedExcursion.duration}
                  </span>
                </span>
                {bokunProduct?.nextDefaultPriceMoney && (
                  <span className="inline-flex items-center gap-2 bg-[#0B0B0B]/90 backdrop-blur-sm px-4 py-2 text-sm">
                    <span className="text-[#C9A24D] font-semibold">
                      À partir de {Math.round(bokunProduct.nextDefaultPriceMoney.amount)} {bokunProduct.nextDefaultPriceMoney.currency}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Loading state */}
              {loadingBokunProduct && (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin mx-auto mb-4" />
                  <p className="text-[#A7A7A7] text-sm">Chargement des détails de l'excursion...</p>
                </div>
              )}

              {/* Description */}
              {!loadingBokunProduct && (
                <div>
                  <p className="text-[#D4D4D4] text-base leading-relaxed mb-4">
                    {bokunProduct?.description || selectedExcursion.description}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-[#A7A7A7]">
                    <Ship className="w-4 h-4 text-[#C9A24D]" />
                    <span>Départ & retour : terminal croisières du Havre</span>
                  </div>
                </div>
              )}

              {/* Galerie photos Bókun */}
              {bokunProduct?.photos && bokunProduct.photos.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {bokunProduct.photos.slice(1, 5).map((photo, idx) => (
                    <div key={idx} className="relative h-20 overflow-hidden rounded">
                      <SafeImage
                        src={photo.derived?.find(d => d.name === 'preview')?.url}
                        fallbackSrc={FALLBACK_IMAGES.dday}
                        alt={photo.description || `Photo ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Formulaire de réservation ou message */}
              {selectedExcursion.bokun_product_id && bokunProduct ? (
                bookingSuccess ? (
                  <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm text-center">
                    <Check className="w-16 h-16 text-[#C9A24D] mx-auto mb-4" />
                    <h4 className="font-['Playfair_Display'] text-2xl font-semibold text-[#F5F5F5] mb-2">
                      Demande envoyée !
                    </h4>
                    <p className="text-[#A7A7A7]">
                      Nous vous contacterons sous peu pour confirmer votre réservation.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleBookingSubmit} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm space-y-4">
                    <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5] mb-4">
                      Réserver cette excursion
                    </h4>

                    {bookingError && (
                      <div className="bg-red-500/20 border border-red-500/50 p-3 rounded text-red-200 text-sm">
                        {bookingError}
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[#E5E5E5]/80 text-sm mb-1 block">Nom complet</label>
                        <Input
                          required
                          value={bookingData.customer_name}
                          onChange={(e) => setBookingData({...bookingData, customer_name: e.target.value})}
                          className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]"
                          placeholder="Jean Dupont"
                        />
                      </div>
                      <div>
                        <label className="text-[#E5E5E5]/80 text-sm mb-1 block">Email</label>
                        <Input
                          required
                          type="email"
                          value={bookingData.user_email}
                          onChange={(e) => setBookingData({...bookingData, user_email: e.target.value})}
                          className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]"
                          placeholder="email@exemple.com"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[#E5E5E5]/80 text-sm mb-1 block">Téléphone</label>
                        <Input
                          required
                          type="tel"
                          value={bookingData.customer_phone}
                          onChange={(e) => setBookingData({...bookingData, customer_phone: e.target.value})}
                          className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]"
                          placeholder="+33 6 12 34 56 78"
                        />
                      </div>
                      <div>
                        <label className="text-[#E5E5E5]/80 text-sm mb-1 block">Date souhaitée</label>
                        <Input
                          required
                          type="date"
                          value={bookingData.booking_date}
                          onChange={(e) => setBookingData({...bookingData, booking_date: e.target.value})}
                          className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[#E5E5E5]/80 text-sm mb-1 block">Nombre de participants</label>
                      <Input
                        required
                        type="number"
                        min="1"
                        max={bokunProduct?.rates?.[0]?.maxPerBooking || 8}
                        value={bookingData.number_of_participants}
                        onChange={(e) => setBookingData({...bookingData, number_of_participants: parseInt(e.target.value)})}
                        className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5]"
                      />
                    </div>

                    <div>
                      <label className="text-[#E5E5E5]/80 text-sm mb-1 block">Demandes spéciales (optionnel)</label>
                      <Textarea
                        value={bookingData.special_requests}
                        onChange={(e) => setBookingData({...bookingData, special_requests: e.target.value})}
                        className="bg-[#1F1F1F] border-[#C9A24D]/20 text-[#E5E5E5] resize-none"
                        rows={3}
                        placeholder="Informations sur votre navire, heure d'embarquement, préférences..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold rounded-none py-3"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Calendar className="w-4 h-4 mr-2" />
                          Demander une réservation
                        </>
                      )}
                    </Button>
                    <p className="text-[#A7A7A7] text-xs text-center">
                      Nous vous contacterons pour confirmer la disponibilité et finaliser votre réservation
                    </p>
                  </form>
                )
              ) : (
                <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
                  <div className="text-center space-y-3">
                    <div className="w-16 h-16 bg-[#C9A24D]/12 flex items-center justify-center mx-auto mb-4">
                      <Calendar className="w-8 h-8 text-[#C9A24D]" />
                    </div>
                    <h4 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5]">
                      Réservation sur demande
                    </h4>
                    <p className="text-sm text-[#A7A7A7] max-w-lg mx-auto mb-6">
                      Cette excursion nécessite un devis personnalisé. Contactez-nous pour organiser votre journée sur mesure.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link to="/booking" className="flex-1">
                        <Button className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold rounded-none py-3">
                          <Calendar className="w-4 h-4 mr-2" />
                          Réserver via formulaire
                        </Button>
                      </Link>
                      <a href="mailto:booking@y-go.fr" className="flex-1">
                        <Button 
                          variant="outline"
                          className="w-full border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 rounded-none py-3"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Demander un devis
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
